<template>
  <div class="ar-root" ref="root" :style="rootStyle">
    <div class="ar-stage" :style="{ transform: 'translateX(' + offsetX + 'px) scale(' + scale + ')' }">
      <!-- Stands: a static, slightly blurred layer under the scene (the SVG
           redraws every frame while the ball moves). -->
      <div class="ar-stands" :style="{ backgroundImage: crowd ? 'url(' + crowd + ')' : 'none' }"></div>
      <!-- Broadcast view of the oval: one flat SVG where every point (grass,
           fence, posts, ball) goes through the same projection (p3). -->
      <svg class="ar-scene" :viewBox="'0 0 ' + STAGE_W + ' ' + STAGE_H" preserveAspectRatio="none">
        <defs>
          <linearGradient :id="uid + 'roof'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#050608" stop-opacity="0.95" />
            <stop offset="1" stop-color="#050608" stop-opacity="0" />
          </linearGradient>
          <linearGradient :id="uid + 'grass'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#3f7a3a" />
            <stop offset="1" stop-color="#4d8c45" />
          </linearGradient>
          <radialGradient :id="uid + 'light'" cx="0.5" cy="0.45" r="0.6">
            <stop offset="0" stop-color="#ffffff" stop-opacity="0.12" />
            <stop offset="1" stop-color="#ffffff" stop-opacity="0" />
          </radialGradient>
          <radialGradient :id="uid + 'ball'" cx="0.35" cy="0.3" r="0.8">
            <stop offset="0" stop-color="#fca5a5" />
            <stop offset="0.5" stop-color="#dc2626" />
            <stop offset="1" stop-color="#7f1d1d" />
          </radialGradient>
          <linearGradient v-if="territory" :id="uid + 'terr'" gradientUnits="userSpaceOnUse" :x1="territory.x1" y1="0" :x2="territory.x2" y2="0">
            <stop offset="0" :stop-color="territory.color" stop-opacity="0" />
            <stop offset="0.7" :stop-color="territory.color" stop-opacity="0.2" />
            <stop offset="1" :stop-color="territory.color" stop-opacity="0.45" />
          </linearGradient>
          <clipPath :id="uid + 'oval-clip'">
            <polygon :points="field.oval" />
          </clipPath>
        </defs>

        <!-- Roof shadow over the stands. -->
        <rect x="0" y="0" :width="STAGE_W" height="60" :fill="'url(#' + uid + 'roof)'" />

        <!-- Grass between the fence and the boundary line, then the fence. -->
        <polygon :points="field.surround" fill="#2f5f2c" />
        <ScrollingBoards :boards="boards" :uid="uid" :logo-url="logoUrl" :board-text="boardText" fill="#0d3b2a" top-fill="#17543c" />

        <!-- The oval: mowing stripes, then every marking. -->
        <polygon :points="field.oval" :fill="'url(#' + uid + 'grass)'" />
        <g :clip-path="'url(#' + uid + 'oval-clip)'">
          <polygon v-for="(s, i) in field.stripes" :key="'st' + i" :points="s" fill="rgba(255,255,255,0.045)" />
          <!-- Inside 50: the zone the team in possession is attacking lights up
               once the ball gets in it. -->
          <polygon v-if="inside50" :points="inside50.points" :fill="inside50.color" class="ar-inside50" />
          <!-- Possession: a wedge in the team's colour pointing at the goal
               it attacks, its tip following the ball. -->
          <polygon v-if="territory" :points="territory.points" :fill="'url(#' + uid + 'terr)'" class="ar-territory" />
          <polygon :points="field.oval" :fill="'url(#' + uid + 'light)'" />
          <image v-if="showSirplayLogo" :href="PITCH_LOGO" :transform="logoDecal" x="-180" y="-50" width="360" height="100" preserveAspectRatio="none" class="arena-logo" />
          <g fill="none" stroke="rgba(255,255,255,0.9)" stroke-linejoin="round" stroke-linecap="round">
            <path v-for="(d, i) in markings.lines" :key="'l' + i" :d="d" stroke-width="0.9" />
          </g>
          <!-- Flat effects on the grass (mark, free kick, centre bounce). -->
          <path v-for="f in floorFx" :key="'ffx' + f.key" :d="f.d" class="ar-fx-ring" fill="none" :stroke="f.color" stroke-width="1.4" />
        </g>
        <path :d="field.outline" fill="none" stroke="rgba(255,255,255,0.95)" stroke-width="1.1" />

        <!-- Goal posts: two tall goal posts, a shorter behind post either side. -->
        <g v-for="g in posts" :key="'gp' + g.side" :class="{ 'ar-posts-shake': postShake === g.side }">
          <line v-for="(p, i) in g.list" :key="'p' + i" :x1="p.base.x" :y1="p.base.y" :x2="p.top.x" :y2="p.top.y"
                stroke="#f8fafc" :stroke-width="p.goal ? 1.5 : 1.1" stroke-linecap="round" />
          <!-- Padding at the foot of each post. -->
          <line v-for="(p, i) in g.list" :key="'pp' + i" :x1="p.base.x" :y1="p.base.y" :x2="p.pad.x" :y2="p.pad.y"
                :stroke="padColor(g.side)" :stroke-width="p.goal ? 2.6 : 2.1" />
        </g>

        <!-- Ball: trail while it flies, shadow on the grass, the ball itself. -->
        <polyline v-if="trail.length > 1" :points="trail.join(' ')" fill="none" stroke="#fde68a" stroke-opacity="0.8" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
        <g v-if="ballVisible">
          <ellipse :cx="ballView.sx" :cy="ballView.sy" :rx="ballView.r * 1.5" :ry="ballView.r * 0.55" fill="rgba(5,20,8,0.4)" />
          <g :transform="'translate(' + ballView.x.toFixed(1) + ',' + ballView.y.toFixed(1) + ') rotate(' + ballSpin.toFixed(0) + ')'">
            <ellipse cx="0" cy="0" :rx="ballView.r * 1.35" :ry="ballView.r * 0.85" :fill="'url(#' + uid + 'ball)'" stroke="rgba(69,10,10,0.7)" stroke-width="0.3" />
            <line :x1="-ballView.r * 0.5" y1="0" :x2="ballView.r * 0.5" y2="0" stroke="rgba(255,255,255,0.85)" stroke-width="0.35" />
          </g>
        </g>

        <!-- Bursts: tackle, ball through the posts, behind. -->
        <circle v-for="f in fx" :key="'fx' + f.key" :cx="f.x" :cy="f.y" :r="f.r" fill="none" :stroke="f.color" stroke-width="1.6" class="ar-fx-burst" />
        <text v-for="f in fxText" :key="'fxt' + f.key" :x="f.x" :y="f.y" text-anchor="middle" class="ar-fx-text" :fill="f.color">{{ f.text }}</text>
      </svg>

      <!-- Label over the ball: the player / incident just played (mark,
           tackle, ...) or the team with the ball. -->
      <div v-if="tag" :key="tag.key" class="ar-tag"
           :style="{ left: ballView.x + 'px', top: (ballView.y - ballView.r - 6) + 'px', background: tag.color, color: textOn(tag.color) }">
        <span v-if="tag.sub" class="ar-tag-sub">{{ tag.sub }}</span>{{ tag.text }}
      </div>

      <!-- Clock. -->
      <div class="ar-clock">
        <b v-if="periodText">{{ periodText }}</b>
        <span>{{ time }}</span>
      </div>

      <!-- Break between quarters: dims the oval for as long as it lasts. -->
      <transition name="ar-card">
        <div v-if="breakCard" key="break" class="ar-card-veil">
          <div class="ar-card">
            <svg class="ar-card-icon" viewBox="0 0 40 40">
              <path d="M6,17 a9,9 0 1 0 18,0 H34 V11 H14 A9,9 0 0 0 6,17 Z" fill="#fff" />
              <circle cx="15" cy="18" r="3" fill="#0b2a17" />
              <path d="M28,11 V7" stroke="#fff" stroke-width="3" stroke-linecap="round" />
            </svg>
            <div class="ar-card-title">{{ breakCard.title }}</div>
            <div class="ar-card-sub">{{ breakCard.sub }}</div>
          </div>
        </div>
      </transition>

      <!-- Goal, two acts on one full-stage panel: 1) "GOAL" with the scoring
           team over its colour and chevrons running towards the posts; 2)
           split screen in the two teams' colours, the scorer's points rolling
           to the new score. -->
      <transition name="ar-goal-panel">
        <div v-if="goalShown" :key="goalShown.key" class="ar-goal-panel" :style="{ '--goal-color': goalShown.scoreSide === '2' ? awayCol : homeCol }">
          <transition name="ar-goal-act" mode="out-in">
            <div v-if="goalAct === 'word'" key="word" class="ar-goal-act ar-goal-act--word">
              <svg class="ar-goal-chevrons" :class="{ 'is-left': goalShown.scoreSide === '2' }" viewBox="0 0 460 236" preserveAspectRatio="none">
                <polyline v-for="x in [40, 150, 260, 370]" :key="'cv' + x" :points="x + ',-10 ' + (x + 118) + ',118 ' + x + ',246'" fill="none" stroke="var(--goal-color)" stroke-width="2.2" />
                <polyline v-for="x in [40, 150, 260, 370]" :key="'cw' + x" :points="x + ',-10 ' + (x + 118) + ',118 ' + x + ',246'" fill="none" stroke="rgba(255,255,255,0.45)" stroke-width="0.8" />
              </svg>
              <div class="ar-goal-words">
                <div class="ar-goal-word">{{ goalShown.title }}</div>
                <div class="ar-goal-team">{{ goalShown.team }} · +6</div>
              </div>
            </div>
            <div v-else key="split" class="ar-goal-act ar-goal-act--split">
              <div class="ar-goal-half" :style="{ background: halfBg(homeCol) }">
                <span class="ar-goal-digit">
                  <transition name="ar-roll" mode="out-in"><span :key="'h' + rollHome" class="ar-goal-digit-value" :class="{ 'is-intro': !goalRolled }" :style="{ color: textOn(homeCol) }">{{ rollHome }}</span></transition>
                </span>
                <span class="ar-goal-gb" :style="{ color: textOn(homeCol) }">{{ goalsBehinds('1') }}</span>
              </div>
              <div class="ar-goal-half ar-goal-half--away" :style="{ background: halfBg(awayCol) }">
                <span class="ar-goal-digit">
                  <transition name="ar-roll" mode="out-in"><span :key="'a' + rollAway" class="ar-goal-digit-value" :class="{ 'is-intro': !goalRolled }" :style="{ color: textOn(awayCol) }">{{ rollAway }}</span></transition>
                </span>
                <span class="ar-goal-gb" :style="{ color: textOn(awayCol) }">{{ goalsBehinds('2') }}</span>
              </div>
            </div>
          </transition>
        </div>
      </transition>

      <!-- Match over: badge that stays up. -->
      <transition name="ar-final">
        <div v-if="finished" class="ar-final">
          <div class="ar-final-badge">
            <svg class="ar-final-icon" viewBox="0 0 40 40">
              <path d="M12,5 H28 V15 a8,8 0 0 1 -16,0 Z" fill="#facc15" />
              <path d="M12,8 H6 a6,6 0 0 0 6,9 M28,8 H34 a6,6 0 0 1 -6,9" fill="none" stroke="#facc15" stroke-width="2.4" />
              <rect x="18" y="22" width="4" height="7" fill="#eab308" />
              <rect x="12" y="29" width="16" height="5" rx="1.5" fill="#eab308" />
            </svg>
            <div class="ar-final-title">{{ $t('statistics.matchfinished') }}</div>
            <div class="ar-final-score">
              <span :class="{ 'is-win': +scoreH > +scoreA }">{{ homeName }} {{ scoreH }}</span>
              <span class="ar-final-sep">-</span>
              <span :class="{ 'is-win': +scoreA > +scoreH }">{{ scoreA }} {{ awayName }}</span>
            </div>
            <div v-if="goalsBehinds('1') || goalsBehinds('2')" class="ar-final-gb">{{ goalsBehinds('1') }} - {{ goalsBehinds('2') }}</div>
            <div v-if="periods.length" class="ar-final-periods">
              <span v-for="(p, i) in periods" :key="'p' + i">{{ p.h }}-{{ p.a }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ScrollingBoards from '@/components/arenamatchtracker/ScrollingBoards.vue';
import { makeProjection, pt, seg, crowdTexture, makeBoard, stageScaleMixin, floorLogo, PITCH_LOGO, sirplayLicenceMixin } from '@/services/matchtracker/broadcastScene';

// Stage = fixed broadcast frame, scaled to the container width (see stageScaleMixin).
const STAGE_W = 460;
const STAGE_H = 236;
// Oval projection (see makeProjection): u spans the oval's length. The camera
// sits high on the wing: the far boundary just under the fence, the near one
// below the frame, both ends of the ground at the frame's edges.
const { proj } = makeProjection({ farY: 56, nearY: 300, farW: 300, nearW: 640, farCx: 230, nearCx: 230 });
// Oval in metres (MCG-like): 160 x 130. X along the length (0 = left goal
// line), Y across (0 = far boundary), H height above the grass.
const FL = 160;
const FW = 130;
const MID_X = FL / 2;
const MID_Y = FW / 2;
const RX = FL / 2;
const RY = FW / 2;
// Nearest the ball goes: the near wing is under the frame's bottom edge.
const PLAY_MAX_Y = 104;
const PLAY_MIN_Y = 8;
const CENTRE_SQUARE = 50;
const ARC_R = 50;
// Goal square 9 m deep, posts 6.4 m apart.
const GOAL_SQ_D = 9;
const POST_GAP = 6.4;
// Posts drawn taller than they are so they read at this size.
const GOAL_POST_H = 13;
const BEHIND_POST_H = 8;
// Fence (ad-boards) a few metres outside the boundary line.
const FENCE = 6;
const BOARD_H = 4.6;
const BALL_R = 1.5;
// Timings (ms), from the ball going through the posts.
const GOAL_PANEL_DELAY = 450;
const GOAL_SPLIT_AT = 2400;
const GOAL_ROLL_AT = 3300;
const GOAL_END_AT = 5600;
const TAG_MS = 3500;
const FX_MS = 900;
// Incident types (see aflIncidents.js).
const T = { GOAL: 9, POSSESSION: 11, KICK: 91, BEHIND: 2032, TACKLE: 2228, HANDBALL: 2299, FREE: 2300, CLEARANCE: 2302, MARK: 2303, HITOUT: 2304 };
// Match-phase pseudo incidents from MatchTracker (second half, kick-off).
const RESTART_TYPES = [9002, 9004];

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const pick = list => list[Math.floor(Math.random() * list.length)];

// 3D point on the ground (metres) -> screen; m = px per metre at that depth.
function p3(X, Y, H = 0) {
  const g = proj(X / FL, Y / FW);
  const m = g.w / FL;
  return { x: g.x, y: g.y - H * m, m };
}
const poly = list => list.map(pt).join(' ');
const pathOf = list => 'M' + list.map(pt).join(' L');
// Point of the ellipse rx x ry round the oval's centre, at angle a.
const onOval = (a, rx = RX, ry = RY) => ({ X: MID_X + Math.cos(a) * rx, Y: MID_Y + Math.sin(a) * ry });
const ovalPath = (rx, ry, n = 72) => {
  const out = [];
  for (let i = 0; i < n; i++) out.push(onOval(Math.PI * 2 * i / n, rx, ry));
  return out;
};
// Keeps a point `margin` metres inside the boundary and on the visible part
// of the ground.
function inPlay(P, margin = 4) {
  let X = P.X;
  let Y = clamp(P.Y, PLAY_MIN_Y, PLAY_MAX_Y);
  const dx = (X - MID_X) / (RX - margin);
  const dy = (Y - MID_Y) / (RY - margin);
  const d = Math.hypot(dx, dy);
  if (d > 1) {
    X = MID_X + (X - MID_X) / d;
    Y = MID_Y + (Y - MID_Y) / d;
  }
  return { X, Y };
}
// Home attacks the right goal, away the left one.
const goalX = end => (end === 'R' ? FL : 0);
const attackEnd = team => (team === '1' ? 'R' : 'L');
const attackDir = team => (team === '1' ? 1 : -1);
const CENTRE = { X: MID_X, Y: MID_Y, H: 0 };
// Point `r` metres out from the goal attacked by `team`, at angle `a`
// (0 = straight in front).
const aroundGoal = (team, r, a) => {
  const dir = attackDir(team);
  return inPlay({ X: goalX(attackEnd(team)) - dir * Math.cos(a) * r, Y: MID_Y + Math.sin(a) * r });
};
// Arc of radius r round the goal at gx, from one boundary to the other
// (clipped to the oval when drawn).
function goalArc(gx, r, n = 30) {
  const dir = gx === 0 ? 1 : -1;
  const out = [];
  for (let i = 0; i <= n; i++) {
    const a = -Math.PI / 2 + Math.PI * i / n;
    out.push({ X: gx + dir * Math.cos(a) * r, Y: MID_Y + Math.sin(a) * r });
  }
  return out;
}

let fxSeq = 0;

export default {
  name: 'AussieRulesArena',
  components: { ScrollingBoards },
  mixins: [stageScaleMixin, sirplayLicenceMixin],
  props: {
    homeName: { type: String, default: '' },
    awayName: { type: String, default: '' },
    // Points (goal = 6, behind = 1).
    scoreH: { type: [String, Number], default: 0 },
    scoreA: { type: [String, Number], default: 0 },
    // Goals / behinds kicked so far, { h, a } each.
    goals: { type: Object, default: () => ({ h: 0, a: 0 }) },
    behinds: { type: Object, default: () => ({ h: 0, a: 0 }) },
    // '1' home / '2' away / null.
    possessionSide: { type: String, default: null },
    time: { type: String, default: '' },
    periodText: { type: String, default: '' },
    // Scoreboard's current period: a new quarter starts with a centre bounce.
    period: { type: Number, default: 0 },
    // Incident on screen (MatchTracker's displayAction): type, side, key, name, player.
    actionType: { type: Number, default: 0 },
    actionSide: { type: String, default: null },
    actionKey: { type: String, default: '' },
    actionText: { type: String, default: '' },
    actionPlayer: { type: String, default: '' },
    // { key, title, team, scoreSide, fromHome, fromAway, homeScore, awayScore }
    goalCelebration: { type: Object, default: null },
    // Quarter / half time break, and its name (Half time, Quarter time).
    isBreak: { type: Boolean, default: false },
    breakText: { type: String, default: '' },
    // Points per quarter so far, [{ h, a }] — shown on the final badge.
    periods: { type: Array, default: () => [] },
    finished: { type: Boolean, default: false },
    homeColor: { type: String, default: '' },
    awayColor: { type: String, default: '' },
    logoUrl: { type: String, default: '' },
    boardText: { type: String, default: 'LIVE' }
  },
  data() {
    return {
      PITCH_LOGO,
      STAGE_W,
      STAGE_H,
      ZOOM: 1,
      crowd: '',
      ball: { ...CENTRE },
      ballVisible: true,
      ballSpin: 0,
      // Scripted play (kick, mark, shot, ...) or null while the ball moves freely.
      seq: null,
      // Team playing the scripted play.
      seqTeam: null,
      wander: null,
      // Where the possession wedge ends: follows the ball with some lag.
      front: MID_X,
      trail: [],
      raf: null,
      fx: [],
      fxText: [],
      floorFx: [],
      fxTimers: [],
      postShake: null,
      tagShown: null,
      tagTimer: null,
      // Team of the last incident: keeps the ball moving its way when the
      // feed has no possession reading.
      lastTeam: null,
      goalShown: null,
      goalAct: 'word',
      goalRolled: false,
      goalTimers: [],
      rollHome: 0,
      rollAway: 0
    };
  },
  watch: {
    actionKey() {
      if (this.finished) return;
      const t = this.actionType;
      const s = this.actionSide;
      const team = s === '1' || s === '2' ? s : null;
      if (RESTART_TYPES.includes(t)) {
        if (!this.goalShown) this.playCentreBounce();
        return;
      }
      // Goals play out from the goal celebration.
      if (!team || t === T.GOAL || this.goalShown) return;
      const other = team === '1' ? '2' : '1';
      if (t !== T.POSSESSION) this.showTag(this.actionText, team, this.actionPlayer);
      this.lastTeam = team;
      if (t === T.KICK) this.playKick(team);
      else if (t === T.HANDBALL) this.playHandball(team);
      else if (t === T.MARK) this.playMark(team);
      else if (t === T.TACKLE) this.playTackle(team);
      else if (t === T.FREE) this.playFreeKick(team);
      else if (t === T.CLEARANCE) this.playClearance(team);
      else if (t === T.HITOUT) this.playHitOut(team);
      else if (t === T.BEHIND) { this.lastTeam = other; this.playShot(team, 'behind'); }
    },
    // The feed moves to the next quarter as soon as the break starts: the
    // centre bounce comes when the break is over (or on a quarter change
    // without one).
    period(n, o) {
      if (n !== o && !this.isBreak && !this.goalShown && !this.finished) this.playCentreBounce();
    },
    isBreak(val) {
      if (val) {
        // Play stops: the ball goes to the centre and waits there.
        this.seq = null;
        this.wander = null;
        this.tagShown = null;
        this.ball = { ...CENTRE };
        this.front = MID_X;
      } else if (!this.goalShown && !this.finished) {
        this.playCentreBounce();
      }
    },
    'goalCelebration.key'(key) {
      const g = this.goalCelebration;
      // A goal is only ever replaced by the next goal, never cut short.
      if (!key || !g || this.finished) return;
      this.startGoal(g);
    },
    'goalCelebration.homeScore'(v) { if (this.goalShown && this.goalRolled) this.rollHome = v; },
    'goalCelebration.awayScore'(v) { if (this.goalShown && this.goalRolled) this.rollAway = v; },
    finished(val) {
      if (!val) return;
      this.seq = null;
      this.ball = { ...CENTRE };
    }
  },
  mounted() {
    this.crowd = crowdTexture('cool');
    this.raf = requestAnimationFrame(this.tick);
  },
  beforeDestroy() {
    cancelAnimationFrame(this.raf);
    clearTimeout(this.tagTimer);
    this.goalTimers.forEach(clearTimeout);
    this.fxTimers.forEach(clearTimeout);
  },
  methods: {
    tint(hex, alpha) {
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (!m) return `rgba(45,95,174,${alpha})`;
      const n = parseInt(m[1], 16);
      return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
    },
    isLight(hex) {
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (!m) return false;
      const n = parseInt(m[1], 16);
      return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) > 185;
    },
    textOn(hex) { return this.isLight(hex) ? '#1f2937' : '#fff'; },
    // Team colour as light on the grass: a white shirt reads as pale grey, a
    // very dark one (navy, black) is lifted toward white, or it would look
    // like a shadow.
    lightCol(team) {
      const hex = this.teamCol(team);
      if (this.isLight(hex)) return '#e2e8f0';
      const m = /^#([0-9a-f]{6})$/i.exec(hex);
      if (!m) return hex;
      const n = parseInt(m[1], 16);
      const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
      if (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2] > 70) return hex;
      return '#' + c.map(v => Math.round(v + (255 - v) * 0.45).toString(16).padStart(2, '0')).join('');
    },
    // Team colour, lighter toward the centre line and darker toward the edge.
    halfBg(hex) {
      return `linear-gradient(90deg, rgba(255,255,255,0.12) 0%, rgba(0,0,0,0.38) 100%), ${hex}`;
    },
    teamName(team) { return team === '1' ? this.homeName : this.awayName; },
    teamCol(team) { return team === '1' ? this.homeCol : (team === '2' ? this.awayCol : '#1e293b'); },
    // Post padding in the colour of the team attacking that end.
    padColor(side) { return side === 'R' ? this.homeCol : this.awayCol; },
    // "goals.behinds", e.g. "10.5".
    goalsBehinds(team) {
      const g = team === '1' ? this.goals.h : this.goals.a;
      const b = team === '1' ? this.behinds.h : this.behinds.a;
      return g || b ? `${g || 0}.${b || 0}` : '';
    },
    // Label over the ball: the player who just played it when the feed names
    // him / her, with the incident above; else just the incident.
    showTag(text, team, player = '') {
      if (!text && !player) return;
      clearTimeout(this.tagTimer);
      const name = String(player || '').replace(/<[^>]*>/g, '');
      this.tagShown = { key: 'tag' + (++fxSeq), text: name || text, sub: name ? text : '', color: this.teamCol(team) };
      this.tagTimer = setTimeout(() => { this.tagShown = null; }, TAG_MS);
    },
    later(fn, ms, list = this.fxTimers) {
      const id = setTimeout(() => {
        const i = list.indexOf(id);
        if (i >= 0) list.splice(i, 1);
        fn();
      }, ms);
      list.push(id);
    },
    burst(X, Y, H, color, r = 7) {
      const p = p3(X, Y, H);
      const key = ++fxSeq;
      this.fx = [...this.fx, { key, x: p.x, y: p.y, r, color }];
      this.later(() => { this.fx = this.fx.filter(f => f.key !== key); }, FX_MS);
    },
    floatText(X, Y, H, text, color) {
      const p = p3(X, Y, H);
      const key = ++fxSeq;
      this.fxText = [...this.fxText, { key, x: p.x, y: p.y, text, color }];
      this.later(() => { this.fxText = this.fxText.filter(f => f.key !== key); }, 1600);
    },
    ring(X, Y, color = '#f0fdf4', r = 4) {
      const key = ++fxSeq;
      const pts = [];
      for (let i = 0; i <= 32; i++) {
        const a = Math.PI * 2 * i / 32;
        pts.push(p3(X + Math.cos(a) * r, Y + Math.sin(a) * r));
      }
      this.floorFx = [...this.floorFx, { key, d: pathOf(pts), color }];
      this.later(() => { this.floorFx = this.floorFx.filter(f => f.key !== key); }, FX_MS);
    },
    onEvent(ev) {
      if (!ev) return;
      const { kind, X, Y, H = 0, side } = ev;
      if (kind === 'ring') this.ring(X, Y, ev.color, ev.r);
      else if (kind === 'tackle') this.burst(X, Y, H, '#fbbf24', 7);
      else if (kind === 'out') this.burst(X, Y, H, '#e2e8f0', 6);
      else if (kind === 'behind') {
        this.burst(X, Y, H, '#e2e8f0', 7);
        this.floatText(X, Y, H + 4, '+1', '#f8fafc');
      } else if (kind === 'goal') {
        this.burst(X, Y, H, '#4ade80', 12);
        this.floatText(X, Y, H + 5, '+6', '#4ade80');
        this.postShake = side;
        this.later(() => { this.postShake = null; }, 1400);
      }
    },
    // One animation loop for everything that moves.
    tick(ts) {
      if (this.seq) this.stepSeq(ts);
      else if (!this.finished && !this.isBreak) this.stepWander(ts);
      else if (this.trail.length) this.trail = [];
      this.front += (this.ball.X - this.front) * 0.04;
      this.raf = requestAnimationFrame(this.tick);
    },
    // Idle: the team in possession works the ball forward — kicks, hand
    // balls, a run with the ball bounced — and switches play once deep in
    // its forward 50.
    stepWander(ts) {
      let w = this.wander;
      if (!w || w.team !== this.playTeam || ts - w.t0 >= w.dur) {
        w = this.nextWander(ts);
        this.wander = w;
      }
      const t = clamp((ts - w.t0) / w.dur, 0, 1);
      const e = w.kind === 'kick' ? t : (w.kind === 'hand' ? 1 - (1 - t) * (1 - t) : t * t * (3 - 2 * t));
      const X = (1 - e) * (1 - e) * w.from.X + 2 * (1 - e) * e * w.ctrl.X + e * e * w.to.X;
      const Y = (1 - e) * (1 - e) * w.from.Y + 2 * (1 - e) * e * w.ctrl.Y + e * e * w.to.Y;
      let H;
      if (w.kind === 'kick') H = 0.8 + w.peak * 4 * t * (1 - t);
      else if (w.kind === 'hand') H = 1 + 1.2 * Math.sin(Math.PI * t);
      // Run: carried at hip height, bounced once on the way.
      else H = 1 - 0.9 * Math.abs(Math.sin(Math.PI * clamp((t - 0.4) * 5, 0, 1)));
      this.ball = { X, Y, H };
      if (w.kind === 'kick') this.ballSpin = (this.ballSpin + 14) % 360;
      this.pushTrail(w.kind === 'kick' && t < 0.85);
    },
    nextWander(ts) {
      const team = this.playTeam;
      const from = { X: this.ball.X, Y: this.ball.Y };
      const changed = this.wander && this.wander.team !== team;
      let to;
      let kind;
      if (team) {
        const dir = attackDir(team);
        const toGoal = Math.abs(goalX(attackEnd(team)) - from.X);
        const r = Math.random();
        // A turnover is played on with a kick; otherwise kick / hand ball / run.
        kind = changed || r < 0.5 ? 'kick' : (r < 0.8 ? 'hand' : 'run');
        if (toGoal < 30) {
          // Deep forward: the ball goes back out / across, looking for a lead.
          to = aroundGoal(team, rand(28, 48), rand(-1, 1));
          if (kind === 'run') kind = 'hand';
        } else if (kind === 'kick') {
          to = inPlay({ X: from.X + dir * rand(22, 45), Y: from.Y + rand(-28, 28) });
        } else if (kind === 'hand') {
          to = inPlay({ X: from.X + dir * rand(2, 8), Y: from.Y + rand(-7, 7) });
        } else {
          to = inPlay({ X: from.X + dir * rand(8, 16), Y: from.Y + rand(-6, 6) });
        }
      } else {
        // No one has it: the ball moves around the middle.
        kind = pick(['hand', 'kick', 'run']);
        to = inPlay({ X: MID_X + rand(-25, 25), Y: MID_Y + rand(-25, 25) });
      }
      const dist = Math.hypot(to.X - from.X, to.Y - from.Y);
      const bend = kind === 'run' ? rand(-0.25, 0.25) * dist : 0;
      const nx = dist ? -(to.Y - from.Y) / dist : 0;
      const ny = dist ? (to.X - from.X) / dist : 0;
      const ctrl = inPlay({ X: (from.X + to.X) / 2 + nx * bend, Y: (from.Y + to.Y) / 2 + ny * bend }, 1);
      const dur = kind === 'kick' ? clamp(dist * 38, 900, 1900)
        : kind === 'hand' ? clamp(dist * 70, 380, 700)
        : clamp(dist * 170, 1200, 2600);
      return { team, from, to, ctrl, kind, peak: clamp(dist * 0.35, 6, 16), t0: ts, dur };
    },
    pushTrail(active) {
      if (active) {
        const p = this.ballView;
        this.trail = [...this.trail.slice(-9), pt({ x: p.x, y: p.y })];
      } else if (this.trail.length) {
        this.trail = this.trail.slice(1);
      }
    },
    // Scripted play: a list of { to, dur, ease, peak, fast, ev } segments
    // starting from wherever the ball is now.
    runSeq(segs, team = null) {
      this.seqTeam = team;
      let from = { ...this.ball };
      const list = segs.map(s => {
        const to = { H: 0, ...(s.to || from) };
        const out = { ...s, from, to };
        from = to;
        return out;
      });
      this.seq = { segs: list, i: 0, t0: null };
    },
    stepSeq(ts) {
      const sq = this.seq;
      if (sq.t0 === null) sq.t0 = ts;
      let sg = sq.segs[sq.i];
      let t = (ts - sq.t0) / sg.dur;
      while (t >= 1) {
        this.onEvent(sg.ev);
        sq.i++;
        if (sq.i >= sq.segs.length) {
          this.ball = { ...sg.to };
          this.seq = null;
          this.seqTeam = null;
          this.wander = null;
          return;
        }
        sq.t0 += sg.dur;
        sg = sq.segs[sq.i];
        t = (ts - sq.t0) / sg.dur;
      }
      let e = t;
      if (sg.ease === 'smooth') e = t * t * (3 - 2 * t);
      else if (sg.ease === 'out') e = 1 - (1 - t) * (1 - t);
      const X = sg.from.X + (sg.to.X - sg.from.X) * e;
      const Y = sg.from.Y + (sg.to.Y - sg.from.Y) * e;
      const H = sg.from.H + (sg.to.H - sg.from.H) * e + 4 * (sg.peak || 0) * t * (1 - t);
      this.ball = { X, Y, H: Math.max(0, H) };
      if (sg.fast) this.ballSpin = (this.ballSpin + 16) % 360;
      this.pushTrail(!!sg.fast);
    },
    // Kick forward by `team` from where the ball is, about `len` metres.
    kickSeg(team, len) {
      return this.kickSegFrom(team, this.ball, len);
    },
    playKick(team) {
      this.runSeq([this.kickSeg(team, rand(25, 45)), { dur: 600 }], team);
    },
    playHandball(team) {
      const dir = attackDir(team);
      const to = inPlay({ X: this.ball.X + dir * rand(3, 8), Y: this.ball.Y + rand(-6, 6) });
      this.runSeq([
        { to: { ...to, H: 1.2 }, dur: 520, fast: true, peak: 0.6 },
        { dur: 500 }
      ], team);
    },
    // Mark: a kick caught on the full, held up high, ring on the spot; the
    // player then goes back to take the kick.
    playMark(team) {
      const k = this.kickSeg(team, rand(25, 40));
      const at = { X: k.to.X, Y: k.to.Y, H: 2.6 };
      this.runSeq([
        { ...k, to: at, ev: { kind: 'ring', X: at.X, Y: at.Y, color: this.teamCol(team) } },
        { dur: 1500 },
        { to: { X: at.X, Y: at.Y, H: 1 }, dur: 400, ease: 'out' }
      ], team);
    },
    // Tackle by `team` on the player with the ball: the ball is jolted loose.
    playTackle(team) {
      const b = { ...this.ball };
      const dir = attackDir(team);
      this.runSeq([
        { to: { X: b.X, Y: b.Y, H: 1 }, dur: 250, ev: { kind: 'tackle', X: b.X, Y: b.Y, H: 1 } },
        { to: { ...inPlay({ X: b.X + dir * rand(1, 3), Y: b.Y + rand(-3, 3) }), H: 0 }, dur: 600, ease: 'out', peak: 0.8 },
        { dur: 900 }
      ], team);
    },
    // Free kick: the umpire's spot, then the kick.
    playFreeKick(team) {
      const b = inPlay(this.ball);
      this.runSeq([
        { to: { ...b, H: 0.8 }, dur: 450, ease: 'out', ev: { kind: 'ring', X: b.X, Y: b.Y, color: this.teamCol(team), r: 5 } },
        { dur: 1300 },
        this.kickSegFrom(team, b, rand(28, 45)),
        { dur: 500 }
      ], team);
    },
    // Kick forward by `team` from `from`, about `len` metres.
    kickSegFrom(team, from, len) {
      const dir = attackDir(team);
      const target = inPlay({ X: from.X + dir * len, Y: from.Y + rand(-20, 20) });
      const dist = Math.hypot(target.X - from.X, target.Y - from.Y);
      return { to: { ...target, H: 1 }, dur: clamp(dist * 38, 800, 1800), fast: true, peak: clamp(dist * 0.35, 6, 16) };
    },
    // Clearance: out of the stoppage with a kick forward.
    playClearance(team) {
      const b = inPlay(this.ball);
      this.runSeq([
        { to: { ...b, H: 0.6 }, dur: 350, ease: 'out', ev: { kind: 'tackle', X: b.X, Y: b.Y, H: 1 } },
        { dur: 350 },
        this.kickSegFrom(team, b, rand(30, 45)),
        { dur: 500 }
      ], team);
    },
    // Hit-out: the ruck taps the ball down to a team-mate at a stoppage.
    playHitOut(team) {
      const b = inPlay(this.ball);
      const dir = attackDir(team);
      const up = { ...b, H: 7 };
      this.runSeq([
        { to: { ...b, H: 0.4 }, dur: 300, ease: 'out' },
        { to: up, dur: 700, ease: 'out' },
        { to: { ...inPlay({ X: b.X + dir * rand(3, 7), Y: b.Y + rand(-5, 5) }), H: 1 }, dur: 650, ease: 'smooth', ev: { kind: 'ring', X: b.X, Y: b.Y, color: this.teamCol(team) } },
        { dur: 500 }
      ], team);
    },
    // Shot at goal by `team`: 'goal' between the goal posts, 'behind' between
    // a goal post and a behind post (then the kick-in by the other team).
    // Returns how long until the ball crosses the line.
    playShot(team, outcome) {
      const gx = goalX(attackEnd(team));
      const dir = attackDir(team);
      const spot = { ...aroundGoal(team, rand(18, 45), rand(-0.75, 0.75)), H: 1 };
      const approach = clamp(Math.hypot(spot.X - this.ball.X, spot.Y - this.ball.Y) * 25, 500, 1400);
      const segs = [{ to: spot, dur: approach, ease: 'smooth' }, { dur: 350 }];
      const lineY = outcome === 'goal'
        ? MID_Y + rand(-POST_GAP / 2 + 0.6, POST_GAP / 2 - 0.6)
        : MID_Y + pick([-1, 1]) * rand(POST_GAP / 2 + 0.8, POST_GAP * 1.5 - 0.6);
      const line = { X: gx, Y: lineY, H: rand(6, 11) };
      const dist = Math.hypot(line.X - spot.X, line.Y - spot.Y);
      const flight = clamp(dist * 40, 800, 1700);
      segs.push({ to: line, dur: flight, fast: true, peak: clamp(dist * 0.2, 3, 9), ev: { kind: outcome, X: gx, Y: lineY, H: line.H, side: attackEnd(team) } });
      segs.push({ to: { X: gx + dir * 3, Y: lineY + rand(-2, 2), H: 0 }, dur: 450, ease: 'out' });
      if (outcome === 'behind') {
        // Kick-in from the goal square.
        const kickIn = { X: gx - dir * GOAL_SQ_D * 0.6, Y: MID_Y };
        segs.push({ dur: 900 });
        segs.push({ to: kickIn, dur: 900, ease: 'smooth' });
        segs.push({ dur: 600 });
        const out = inPlay({ X: kickIn.X - dir * rand(30, 45), Y: MID_Y + rand(-25, 25) });
        segs.push({ to: { ...out, H: 1 }, dur: 1500, fast: true, peak: 12 });
      } else {
        segs.push({ dur: 2400 });
      }
      this.runSeq(segs, team);
      return approach + 350 + flight;
    },
    // Centre bounce (start of a quarter, after a goal): the umpire bounces
    // the ball high in the centre circle.
    playCentreBounce() {
      this.runSeq([
        { to: { X: CENTRE.X, Y: CENTRE.Y, H: 1.2 }, dur: 900, ease: 'smooth' },
        { dur: 600 },
        { to: { X: CENTRE.X, Y: CENTRE.Y, H: 0 }, dur: 200, ev: { kind: 'ring', X: CENTRE.X, Y: CENTRE.Y, r: 5 } },
        { to: { X: CENTRE.X, Y: CENTRE.Y, H: 12 }, dur: 700, ease: 'out' },
        { to: { X: CENTRE.X, Y: CENTRE.Y, H: 6 }, dur: 450 },
        { dur: 300 }
      ]);
    },
    startGoal(g) {
      this.goalTimers.forEach(clearTimeout);
      this.goalTimers = [];
      this.tagShown = null;
      const scorer = g.scoreSide === '2' ? '2' : '1';
      const through = this.playShot(scorer, 'goal');
      const at = ms => through + ms;
      this.later(() => {
        this.goalShown = g;
        this.goalAct = 'word';
        this.goalRolled = false;
        this.rollHome = g.fromHome;
        this.rollAway = g.fromAway;
      }, at(GOAL_PANEL_DELAY), this.goalTimers);
      this.later(() => { this.goalAct = 'split'; }, at(GOAL_SPLIT_AT), this.goalTimers);
      this.later(() => {
        const cur = this.goalCelebration && this.goalCelebration.key === g.key ? this.goalCelebration : g;
        this.goalRolled = true;
        this.rollHome = cur.homeScore;
        this.rollAway = cur.awayScore;
      }, at(GOAL_ROLL_AT), this.goalTimers);
      this.later(() => {
        this.goalShown = null;
        // Centre bounce to restart.
        this.seq = null;
        this.ball = { ...CENTRE };
        this.front = MID_X;
        this.wander = null;
        this.lastTeam = null;
        this.playCentreBounce();
      }, at(GOAL_END_AT), this.goalTimers);
    }
  },
  computed: {
    logoDecal() {
      return floorLogo(p3, MID_X, MID_Y - 22, 4);
    },
    // SVG ids are document-global: keep each instance's gradients apart.
    uid() { return 'afl' + this._uid + '-'; },
    homeCol() { return this.homeColor || '#2d5fae'; },
    awayCol() { return this.awayColor || '#c0392b'; },
    // Team moving the ball: the feed's possession, else the last incident's.
    playTeam() {
      const p = this.possessionSide;
      if (p === '1' || p === '2') return p;
      return this.lastTeam;
    },
    breakCard() {
      if (!this.isBreak || this.finished || this.goalShown) return null;
      return { title: this.breakText || this.$t('afl.break'), sub: `${this.scoreH} - ${this.scoreA}` };
    },
    // Label over the ball: the incident just played, else who has the ball.
    tag() {
      if (this.goalShown || this.finished || this.isBreak) return null;
      if (this.tagShown) return this.tagShown;
      const team = this.seqTeam || this.playTeam;
      if (!team) return null;
      return { key: 'team' + team, text: this.teamName(team), sub: '', color: this.teamCol(team) };
    },
    rootStyle() {
      return { height: (STAGE_H * this.scale) + 'px' };
    },
    field() {
      const oval = ovalPath(RX, RY).map(p => p3(p.X, p.Y));
      // Mowing stripes across the ground, 10 m wide every other one.
      const stripes = [];
      for (let X = 0; X < FL; X += 20) {
        stripes.push(poly([p3(X, -2), p3(X + 10, -2), p3(X + 10, FW + 2), p3(X, FW + 2)]));
      }
      return {
        oval: poly(oval),
        outline: pathOf(oval) + ' Z',
        surround: poly(ovalPath(RX + FENCE + 1, RY + FENCE + 1).map(p => p3(p.X, p.Y))),
        stripes
      };
    },
    // Ad-boards along the fence, the far side of the ground: short straight
    // boards following the curve.
    boards() {
      const out = [];
      const n = 14;
      const a0 = Math.PI * 0.93;
      const a1 = Math.PI * 2.07;
      for (let i = 0; i < n; i++) {
        const a = onOval(a0 + (a1 - a0) * i / n, RX + FENCE, RY + FENCE);
        const b = onOval(a0 + (a1 - a0) * (i + 1) / n, RX + FENCE, RY + FENCE);
        const pa = p3(a.X, a.Y);
        const pb = p3(b.X, b.Y);
        out.push(makeBoard('f' + i, pa, pb, pa.y - p3(a.X, a.Y, BOARD_H).y, pb.y - p3(b.X, b.Y, BOARD_H).y, 1, 24));
      }
      return out;
    },
    markings() {
      const lines = [];
      const h = CENTRE_SQUARE / 2;
      // Centre square, centre circles and the line through them.
      lines.push(pathOf([p3(MID_X - h, MID_Y - h), p3(MID_X + h, MID_Y - h), p3(MID_X + h, MID_Y + h), p3(MID_X - h, MID_Y + h)]) + ' Z');
      [1.5, 5].forEach(r => lines.push(pathOf(ovalPath(r, r, 36).map(p => p3(p.X, p.Y))) + ' Z'));
      lines.push(seg(p3(MID_X - 5, MID_Y), p3(MID_X + 5, MID_Y)));
      [0, FL].forEach(gx => {
        const dir = gx === 0 ? 1 : -1;
        // 50-metre arc and goal square.
        lines.push(pathOf(goalArc(gx, ARC_R).map(p => p3(p.X, p.Y))));
        lines.push(pathOf([p3(gx, MID_Y - POST_GAP / 2), p3(gx + dir * GOAL_SQ_D, MID_Y - POST_GAP / 2), p3(gx + dir * GOAL_SQ_D, MID_Y + POST_GAP / 2), p3(gx, MID_Y + POST_GAP / 2)]));
        // Goal line between the behind posts.
        lines.push(seg(p3(gx, MID_Y - POST_GAP * 1.5), p3(gx, MID_Y + POST_GAP * 1.5)));
      });
      return { lines };
    },
    posts() {
      return ['L', 'R'].map(side => {
        const gx = goalX(side);
        const list = [-1.5, -0.5, 0.5, 1.5].map(k => {
          const goal = Math.abs(k) < 1;
          const Y = MID_Y + k * POST_GAP;
          const H = goal ? GOAL_POST_H : BEHIND_POST_H;
          return { goal, base: p3(gx, Y), top: p3(gx, Y, H), pad: p3(gx, Y, 1.8) };
        });
        return { side, list };
      });
    },
    ballView() {
      const b = this.ball;
      const g = p3(b.X, b.Y, 0);
      const up = p3(b.X, b.Y, b.H + BALL_R);
      return { x: up.x, y: up.y, sx: g.x, sy: g.y, r: BALL_R * g.m };
    },
    territory() {
      const team = this.playTeam;
      if (!team || this.finished || this.goalShown || this.isBreak) return null;
      const x0 = team === '1' ? -2 : FL + 2;
      const dir = attackDir(team);
      const front = clamp(this.front + dir * 6, 4, FL - 4);
      // Wedge: full width behind, narrowing to a point just past the ball.
      return {
        points: poly([p3(x0, -2), p3(front - dir * 18, -2), p3(front, clamp(this.ball.Y, 10, FW - 10)), p3(front - dir * 18, FW + 2), p3(x0, FW + 2)]),
        x1: p3(x0, MID_Y).x.toFixed(1),
        x2: p3(front, MID_Y).x.toFixed(1),
        color: this.lightCol(team)
      };
    },
    // Ball inside the forward 50 of the team in possession: that zone glows.
    inside50() {
      const team = this.seqTeam || this.playTeam;
      if (!team || this.finished || this.goalShown || this.isBreak) return null;
      const gx = goalX(attackEnd(team));
      if (Math.hypot(this.ball.X - gx, this.ball.Y - MID_Y) > ARC_R) return null;
      const arc = goalArc(gx, ARC_R, 30);
      return {
        points: poly([p3(gx, MID_Y - ARC_R), ...arc.map(p => p3(p.X, p.Y)), p3(gx, MID_Y + ARC_R)]),
        color: this.tint(this.lightCol(team), 0.22)
      };
    }
  }
};
</script>

<style scoped>
.ar-root { position: relative; width: 100%; overflow: hidden; background: #101418; }
.ar-stage { position: absolute; top: 0; left: 0; width: 460px; height: 236px; transform-origin: 0 0; font-family: Roboto, Arial, sans-serif; }
.ar-stands { position: absolute; inset: -2px; background-color: #1a242e; background-size: 90px 45px; background-repeat: repeat; filter: blur(0.6px) brightness(0.75); }
.ar-scene { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

.ar-territory { animation: ar-territory 2.6s ease-in-out infinite; }
@keyframes ar-territory { 0%, 100% { opacity: 0.75; } 50% { opacity: 1; } }
.ar-inside50 { animation: ar-inside50 0.5s ease-out both; }
@keyframes ar-inside50 { from { opacity: 0; } to { opacity: 1; } }
.ar-posts-shake { animation: ar-shake 0.12s linear 10; }
@keyframes ar-shake { 0%, 100% { transform: translate(0, 0); } 25% { transform: translate(0.5px, -0.3px); } 75% { transform: translate(-0.5px, 0.3px); } }
.ar-fx-burst { transform-box: fill-box; transform-origin: 50% 50%; animation: ar-burst 0.9s ease-out forwards; }
@keyframes ar-burst { from { transform: scale(0.2); opacity: 1; } to { transform: scale(1.8); opacity: 0; } }
.ar-fx-ring { transform-box: fill-box; transform-origin: 50% 50%; animation: ar-ring 0.9s ease-out forwards; }
@keyframes ar-ring { from { transform: scale(0.2); opacity: 1; } to { transform: scale(1.6); opacity: 0; } }
.ar-fx-text { font: italic 900 13px 'Roboto', sans-serif; paint-order: stroke; stroke: rgba(0, 0, 0, 0.55); stroke-width: 2px; animation: ar-fx-text 1.6s ease-out forwards; }
@keyframes ar-fx-text { 0% { opacity: 0; transform: translateY(4px); } 20% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-12px); } }

/* Label over the ball, in the team's colour. */
.ar-tag {
  position: absolute; z-index: 3; transform: translate(-50%, -100%); pointer-events: none;
  display: flex; flex-direction: column; align-items: center;
  padding: 2px 8px; border-radius: 4px; white-space: nowrap;
  font: italic 800 var(--font-size-md)/1.2 'Roboto', sans-serif;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  transition: left 0.08s linear, top 0.08s linear;
  animation: ar-tag-in 0.3s cubic-bezier(.2, .8, .3, 1.2) both;
}
.ar-tag-sub { font: 700 var(--font-size-xxs)/1.1 'Roboto', sans-serif; opacity: 0.85; text-transform: uppercase; }
@keyframes ar-tag-in { from { opacity: 0; transform: translate(-50%, -80%) scale(0.8); } to { opacity: 1; transform: translate(-50%, -100%) scale(1); } }

/* Clock. */
.ar-clock {
  position: absolute; top: 6px; left: 50%; transform: translateX(-50%); z-index: 2;
  display: flex; align-items: center; gap: 6px; padding: 2px 9px;
  background: rgba(0, 0, 0, 0.7); border-radius: 3px; border-top: 2px solid #f59e0b;
  font: 700 var(--font-size-3xl)/1.25 'Roboto Mono', 'Roboto', monospace; color: #4ade80;
}
.ar-clock b { color: #fff; font-family: Roboto, Arial, sans-serif; }

/* Break card. */
.ar-card-veil { position: absolute; inset: 0; z-index: 4; display: flex; align-items: center; justify-content: center; background: rgba(0, 0, 0, 0.32); }
.ar-card {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  min-width: 170px; padding: 10px 26px 11px; border-radius: 6px;
  background: rgba(11, 42, 23, 0.94); border-bottom: 3px solid #94a3b8;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45);
}
.ar-card-icon { width: 40px; height: 40px; margin-bottom: 2px; animation: ar-icon 0.7s cubic-bezier(.2, 1.5, .4, 1) 0.1s both; }
@keyframes ar-icon { from { transform: scale(0.3) rotate(-20deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
.ar-card-title { font: italic 900 var(--font-size-9xl)/1.05 'Roboto', sans-serif; color: #fff; white-space: nowrap; animation: ar-rise 0.4s ease-out 0.2s both; }
.ar-card-sub { font: 700 var(--font-size-4xl)/1.2 'Roboto', sans-serif; color: #86efac; animation: ar-rise 0.4s ease-out 0.3s both; }
@keyframes ar-rise { from { transform: translateY(8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.ar-card-enter-active { transition: opacity 0.25s ease; }
.ar-card-enter-active .ar-card { animation: ar-card-in 0.45s cubic-bezier(.2, 1.3, .4, 1) both; }
.ar-card-leave-active { transition: opacity 0.35s ease; }
.ar-card-enter, .ar-card-leave-to { opacity: 0; }
@keyframes ar-card-in { from { transform: scale(0.7); } to { transform: scale(1); } }

/* Goal celebration. */
.ar-goal-panel { position: absolute; inset: 0; z-index: 6; overflow: hidden; background: #0b0b0d; }
.ar-goal-act { position: absolute; inset: 0; }
.ar-goal-act--word {
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.72) 100%), var(--goal-color);
}
/* Chevrons running towards the posts the team kicked the goal at. */
.ar-goal-chevrons { position: absolute; inset: 0; width: 100%; height: 100%; animation: ar-chevrons 2.2s linear both; filter: drop-shadow(0 0 3px var(--goal-color)); }
.ar-goal-chevrons.is-left { transform: scaleX(-1); animation-name: ar-chevrons-left; }
@keyframes ar-chevrons { from { transform: translateX(-60px); } to { transform: translateX(40px); } }
@keyframes ar-chevrons-left { from { transform: scaleX(-1) translateX(-60px); } to { transform: scaleX(-1) translateX(40px); } }
.ar-goal-words { position: relative; display: flex; flex-direction: column; align-items: flex-start; }
.ar-goal-word {
  font: italic 900 var(--font-size-19xl)/0.95 'Roboto', sans-serif; color: #f1f5f9; text-transform: uppercase;
  text-shadow: 0 3px 0 rgba(0, 0, 0, 0.25);
  animation: ar-goal-word 0.55s cubic-bezier(.2, 1.4, .4, 1) 0.15s both;
}
@keyframes ar-goal-word { from { opacity: 0; transform: translateX(60px) scale(1.6); } to { opacity: 1; transform: translateX(0) scale(1); } }
.ar-goal-team {
  margin-top: 4px; margin-left: 6px; max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  font: italic 800 var(--font-size-8xl)/1.2 'Roboto', sans-serif; color: rgba(255, 255, 255, 0.85);
  animation: ar-goal-team 0.45s ease-out 0.5s both;
}
@keyframes ar-goal-team { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.ar-goal-act-enter-active { transition: opacity 0.3s ease; }
.ar-goal-act-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.ar-goal-act-enter { opacity: 0; }
.ar-goal-act-leave-to { opacity: 0; transform: translateY(-18px); }

.ar-goal-act--split { display: flex; }
.ar-goal-half { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; animation: ar-half-in 0.45s cubic-bezier(.2, .9, .2, 1) both; }
.ar-goal-half--away { animation-delay: 0.08s; }
@keyframes ar-half-in { from { transform: translateY(100%); } to { transform: translateY(0); } }
/* Points can have three digits. */
.ar-goal-digit { position: relative; width: 200px; height: 100px; overflow: hidden; }
.ar-goal-digit-value {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font: italic 900 calc(var(--font-size-20xl) * 1.4)/1 'Roboto', sans-serif; font-variant-numeric: tabular-nums;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}
.ar-goal-gb { font: italic 800 var(--font-size-6xl)/1.1 'Roboto', sans-serif; opacity: 0.85; animation: ar-rise 0.4s ease-out 0.6s both; }
.ar-goal-digit-value.is-intro { animation: ar-roll-up 0.55s cubic-bezier(.3, 1.3, .5, 1) 0.4s both; }
@keyframes ar-roll-up { from { transform: translateY(110%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.ar-roll-leave-active { transition: transform 0.22s cubic-bezier(.5, 0, .9, .4); }
.ar-roll-leave-to { transform: translateY(-110%); }
.ar-roll-enter-active { transition: transform 0.38s cubic-bezier(.15, .85, .35, 1); }
.ar-roll-enter { transform: translateY(110%); }
.ar-goal-panel-enter-active { transition: transform 0.5s cubic-bezier(.2, .9, .2, 1); }
.ar-goal-panel-leave-active { transition: transform 0.45s cubic-bezier(.6, 0, .8, .3); }
.ar-goal-panel-enter { transform: translateX(-105%); }
.ar-goal-panel-leave-to { transform: translateX(105%); }

/* Match over. */
.ar-final { position: absolute; inset: 0; z-index: 7; display: flex; align-items: center; justify-content: center; background: rgba(5, 10, 16, 0.62); }
.ar-final-badge {
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 14px 28px 16px; max-width: 420px;
  background: linear-gradient(160deg, #1f2937 0%, #0b1220 100%); border: 2px solid #facc15; border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.15), 0 12px 30px rgba(0, 0, 0, 0.6);
  animation: ar-final-in 0.7s cubic-bezier(.2, 1.4, .4, 1) both;
}
.ar-final-icon { width: 40px; height: 40px; }
.ar-final-title { color: #facc15; font-size: var(--font-size-10xl); font-weight: 900; text-transform: uppercase; white-space: nowrap; }
.ar-final-score { display: flex; gap: 8px; color: #cbd5e1; font-size: var(--font-size-5xl); font-weight: 700; white-space: nowrap; }
.ar-final-score .is-win { color: #fff; }
.ar-final-sep { opacity: 0.6; }
.ar-final-gb { color: #cbd5e1; font-size: var(--font-size-3xl); font-weight: 700; }
.ar-final-periods { display: flex; gap: 10px; margin-top: 2px; color: #94a3b8; font-size: var(--font-size-2xl); font-weight: 700; }
@keyframes ar-final-in { from { transform: scale(0.3) rotate(-6deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
.ar-final-enter-active { transition: opacity 0.3s; }
.ar-final-enter { opacity: 0; }
.arena-logo { opacity: 0.9; pointer-events: none; }
</style>
